import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { HexColorPicker } from 'react-colorful';
import { useDesignStore } from '../../store/designStore';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import type { ProductVariantDTO } from '@customedge/shared';

interface VariantSelectorProps {
  // 'stack' (default) — one variant per block, label above a wrapped grid of swatches.
  // 'inline' — glassy, label-and-slide-row look; used by Model3DPreview (inlineMode='all')
  // and Model3DPreviewMini (inlineMode='accordion').
  layout?: 'stack' | 'inline';
  // Only meaningful when layout='inline'. 'accordion' — one Variant's row open at a time
  // behind a toggle button (mini widget). 'all' — every row shown at once (full 3D tab).
  inlineMode?: 'all' | 'accordion';
}

// Object Variants picker — shown inside the 3D preview panels (Model3DPreview
// and Model3DPreviewMini). Gates itself on the global `object_variants` Feature
// flag and hides when there are no variants to show.
export function VariantSelector({ layout = 'stack', inlineMode = 'accordion' }: VariantSelectorProps) {
  const variants = useDesignStore(s => s.variants);
  const selected = useDesignStore(s => s.selectedVariantOptions);
  const setVariantOption = useDesignStore(s => s.setVariantOption);
  const features = useFeatureFlags();

  const visible = features === null || features.has('object_variants');
  if (!visible || !variants.length) return null;

  if (layout === 'inline') {
    return <InlineVariantPanel variants={variants} selected={selected} setVariantOption={setVariantOption} mode={inlineMode} />;
  }

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', gap: 10,
      background: 'rgba(255,255,255,0.92)', borderRadius: 8, padding: 10,
      boxShadow: '0 1px 6px rgba(0,0,0,0.15)', maxWidth: 220,
    }}>
      {variants.map(v => (
        <div key={v.id}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', marginBottom: 6 }}>{v.label}</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {v.options.map(o => {
              const isSelected = selected[v.id] === o.id;
              return (
                <button
                  key={o.id}
                  onClick={() => setVariantOption(v.id, o.id)}
                  title={o.name}
                  style={{
                    width: 28, height: 28, borderRadius: '50%', cursor: 'pointer',
                    background: o.type === 'texture' && o.textureUrl ? `url(${o.textureUrl}) center/cover` : (o.colorHex ?? '#d4d4d8'),
                    border: isSelected ? '2px solid var(--selection)' : '1px solid #cbd5e1',
                    boxShadow: isSelected ? '0 0 0 2px var(--selection-soft)' : 'none',
                  }}
                />
              );
            })}
            {v.allowCustomColorPicker && (
              <ColorPickerSwatch
                size={28}
                title={v.label}
                value={selected[v.id]?.startsWith('custom:') ? selected[v.id].slice(7) : undefined}
                onChange={hex => setVariantOption(v.id, `custom:${hex}`)}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// Collapsed-by-default "Select Variant" pill that floats over the live 3D
// canvas (the mini widget passes a glassy, translucent look is achieved here
// rather than the opaque card the 'stack'/full-tab layout uses, since this
// sits ON TOP of the model instead of below it — keeping the widget's overall
// height down to just the canvas + header). Opening it reveals one colored
// tag per Variant (the same "tag, then show only the active one below" idea
// `Model3DUVEditor`'s admin UI already uses elsewhere in this app) plus that
// Variant's slide-to-browse option row; only one Variant's options are ever
// shown at a time so the panel never grows tall enough to cover the model.
const glassyPanelStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 10,
  background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
  border: '1px solid rgba(255,255,255,0.6)', borderRadius: 12, padding: 10,
  boxShadow: '0 1px 6px rgba(0,0,0,0.15)',
};

function InlineVariantPanel({ variants, selected, setVariantOption, mode }: {
  variants: ProductVariantDTO[];
  selected: Record<string, string>;
  setVariantOption: (variantId: string, optionId: string) => void;
  mode: 'all' | 'accordion';
}) {
  const [open, setOpen] = useState(false);
  const [activeVariantId, setActiveVariantId] = useState(variants[0]?.id ?? null);
  const activeVariant = variants.find(v => v.id === activeVariantId) ?? variants[0];

  if (mode === 'all') {
    return (
      <div style={glassyPanelStyle}>
        {variants.map(v => (
          <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#374151', flexShrink: 0, minWidth: 50 }}>{v.label}</div>
            <VariantOptionRow
              variant={v}
              selected={selected[v.id]}
              onSelect={optionId => setVariantOption(v.id, optionId)}
              swatchSize={42}
            />
          </div>
        ))}
      </div>
    );
  }

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{
          display: 'flex', alignItems: 'center', gap: 6, alignSelf: 'flex-start',
          background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
          border: '1px solid rgba(255,255,255,0.6)', borderRadius: 999, padding: '8px 14px',
          fontSize: 13, fontWeight: 600, color: '#1e293b', cursor: 'pointer',
          boxShadow: '0 1px 6px rgba(0,0,0,0.15)',
        }}
      >
        Select Variant ▾
      </button>
    );
  }

  return (
    <div style={glassyPanelStyle}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {variants.map(v => {
            const isActive = activeVariant?.id === v.id;
            return (
              <button
                key={v.id}
                onClick={() => setActiveVariantId(v.id)}
                style={{
                  padding: '5px 11px', borderRadius: 999, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                  border: isActive ? '1px solid var(--selection)' : '1px solid rgba(100,116,139,0.4)',
                  background: isActive ? 'var(--selection)' : 'transparent',
                  color: isActive ? '#fff' : '#374151',
                }}
              >
                {v.label}
              </button>
            );
          })}
        </div>
        <button onClick={() => setOpen(false)} title="Close" style={{
          width: 22, height: 22, borderRadius: 6, border: 'none', background: 'transparent',
          color: '#475569', fontSize: 14, fontWeight: 700, cursor: 'pointer', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>×</button>
      </div>

      {activeVariant && (
        <VariantOptionRow
          variant={activeVariant}
          selected={selected[activeVariant.id]}
          onSelect={optionId => setVariantOption(activeVariant.id, optionId)}
          swatchSize={42}
          visibleLimit={6}
        />
      )}
    </div>
  );
}

const ROW_GAP = 8;
const DEFAULT_VISIBLE_LIMIT = 4;

// Lightens (positive `percent`) or darkens (negative) a `#rrggbb` hex color —
// used to build the metallic swatches' light/dark sheen bands from the
// option's own single stored `colorHex`, no extra asset needed.
function shadeHex(hex: string, percent: number): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const clamp = (v: number) => Math.max(0, Math.min(255, v));
  const r = clamp((num >> 16) + amt);
  const g = clamp(((num >> 8) & 0x00ff) + amt);
  const b = clamp((num & 0x0000ff) + amt);
  return `#${(0x1000000 + r * 0x10000 + g * 0x100 + b).toString(16).slice(1)}`;
}

// A flat `colorHex` reads identically for 'standard' and 'metallic' options —
// the only way to tell them apart was the (tiny, easy-to-miss) name tooltip.
// Builds a diagonal light/dark/light band gradient instead, the same visual
// shorthand real swatch books use for "metallic" vs "matte" finishes, so the
// two read as different materials at a glance, not just different colors.
function metallicBackground(hex: string): string {
  const light = shadeHex(hex, 55);
  const dark = shadeHex(hex, -35);
  return `linear-gradient(135deg, ${light} 0%, ${hex} 28%, ${dark} 50%, ${hex} 72%, ${light} 100%)`;
}

// Picks a readable pipette-icon color (near-black or near-white) against
// whatever flat color currently fills the swatch — the same luminance-based
// contrast approach `readableTextColor()` (admin's `AreaCanvas.tsx`) already
// uses for tag text, applied here so the icon never disappears against a
// dark custom pick.
function readableIconColor(hex: string): string {
  const num = parseInt(hex.replace('#', ''), 16);
  const r = (num >> 16) & 0xff, g = (num >> 8) & 0xff, b = num & 0xff;
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.6 ? '#1e293b' : '#ffffff';
}

// "Allow customer free color pick" (admin's `ProductVariant.allowCustomColorPicker`)
// renders this swatch alongside the curated option swatches.
//
// Two real bugs fixed here, both stemming from the original native
// `<input type="color">`:
// 1. Every browser draws its OWN small internal rectangle for that input
//    (Chrome/Firefox/Safari each ignore `border-radius` on the inner swatch
//    differently), so next to the perfectly circular curated swatches it
//    always looked like a stray, unstyled form control rather than an
//    intentional "pick any color" option.
// 2. Worse, reported live: clicking it inside the floating mini 3D widget (or
//    the full 3D Preview tab's inline panel) made the OS-native color dialog
//    open clipped/hidden — both panels position this swatch via a
//    `position:absolute` ancestor sitting inside an `overflow:hidden` card
//    (`Model3DPreviewMini.tsx`'s outer widget div), and several browsers
//    anchor/clip a native `<input type="color">` popup against that same
//    clipping ancestor instead of the viewport, so the picker effectively
//    never became visible/usable from there.
// Fixed by dropping the native input/dialog entirely in favor of the same
// `react-colorful` popover `ColorPicker.tsx` (the text/shape fill-color tool)
// already uses elsewhere in this app — but rendered via a `position:fixed`
// popover anchored to this swatch's own measured screen position, so it
// always escapes any ancestor's `overflow:hidden`/transform stacking context
// regardless of which panel it's opened from.
function ColorPickerSwatch({ size, title, value, onChange }: {
  size: number;
  title: string;
  value: string | undefined;
  onChange: (hex: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null);
  const [hex, setHex] = useState(value ?? '#d4d4d8');
  const swatchRef = useRef<HTMLButtonElement>(null);

  useEffect(() => { setHex(value ?? '#d4d4d8'); }, [value]);

  const openPicker = () => {
    const rect = swatchRef.current?.getBoundingClientRect();
    if (!rect) return;
    // Prefers opening upward (these swatches usually sit near the bottom of
    // a small floating panel) but falls back below if there isn't enough
    // room above — clamped to the viewport either way so it can never render
    // partly off-screen, the exact failure mode being fixed here.
    const popoverHeight = 230;
    const opensUp = rect.top - popoverHeight > 8;
    setPopoverPos({
      top: opensUp ? rect.top - popoverHeight - 8 : Math.min(rect.bottom + 8, window.innerHeight - popoverHeight - 8),
      left: Math.min(Math.max(rect.left - 90 + rect.width / 2, 8), window.innerWidth - 196),
    });
    setOpen(true);
  };

  const hasValue = !!value;
  const iconColor = hasValue ? readableIconColor(value!) : '#ffffff';

  return (
    <>
      <button
        ref={swatchRef}
        type="button"
        title={title}
        onClick={openPicker}
        style={{
          position: 'relative', width: size, height: size, borderRadius: '50%', flexShrink: 0,
          cursor: 'pointer', border: hasValue ? '2px solid var(--selection)' : '1px solid #cbd5e1',
          boxShadow: hasValue ? '0 0 0 2px var(--selection-soft)' : 'none',
          background: hasValue
            ? value
            : 'conic-gradient(from 0deg, #ff0000, #ff9900, #ffee00, #33ff00, #00fff7, #0066ff, #9900ff, #ff0000)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
        }}
      >
        {/* Pipette icon — communicates "pick any color" at a glance, distinct from
            the plain filled circles curated swatches use. */}
        <svg width={Math.round(size * 0.52)} height={Math.round(size * 0.52)} viewBox="0 0 24 24" fill="none" style={{ pointerEvents: 'none' }}>
          <path
            d="M19.4 4.6a2.3 2.3 0 0 0-3.25 0l-2.1 2.1-1-1-1.42 1.42 1 1-7.6 7.6a1 1 0 0 0-.27.48L3.5 20.5l4.3-1.26a1 1 0 0 0 .48-.27l7.6-7.6 1 1 1.42-1.42-1-1 2.1-2.1a2.3 2.3 0 0 0 0-3.25Z"
            stroke={iconColor}
            strokeWidth={1.6}
            strokeLinejoin="round"
          />
        </svg>
      </button>

      {open && popoverPos && (
        <ColorPickerPopover
          hex={hex}
          top={popoverPos.top}
          left={popoverPos.left}
          onChange={h => { setHex(h); onChange(h); }}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

// Rendered via `createPortal` straight onto `document.body` with
// `position:fixed` viewport coordinates — deliberately NOT a child of the
// swatch's own DOM subtree, so none of the floating mini 3D widget's
// `overflow:hidden`/`position:absolute` ancestors (or the full 3D Preview
// tab's own panel, which has the same shape) can clip or mis-anchor it the
// way the native color-input dialog was. Closes on outside click or Escape,
// the same convention `ColorPicker.tsx`'s popover (text/shape fill color)
// already uses, just portaled instead of a same-tree `position:absolute`
// child, since that ancestor-clipping problem doesn't exist for that tool's
// own panels.
function ColorPickerPopover({ hex, top, left, onChange, onClose }: {
  hex: string;
  top: number;
  left: number;
  onChange: (hex: string) => void;
  onClose: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onPointerDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKeyDown = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [onClose]);

  return createPortal(
    <div
      ref={ref}
      style={{
        position: 'fixed', top, left, zIndex: 2000, width: 180,
        background: '#ffffff', border: '1px solid #e2e8f0', borderRadius: 10,
        padding: 10, boxShadow: '0 8px 24px rgba(15,23,42,0.25)',
      }}
    >
      <HexColorPicker color={hex} onChange={onChange} style={{ width: '100%', height: 140 }} />
      <input
        value={hex}
        onChange={e => onChange(e.target.value)}
        style={{
          marginTop: 8, width: '100%', boxSizing: 'border-box', padding: '4px 8px',
          border: '1px solid #cbd5e1', borderRadius: 4, fontSize: 12, fontFamily: 'monospace',
        }}
      />
    </div>,
    document.body,
  );
}

// One Variant's options as a single non-wrapping row that scrolls
// horizontally — native touch swipe works for free; mouse users get the same
// "slide" behavior via click-and-drag (`onMouseDown`/`onMouseMove`), since a
// trackpad/mouse can't swipe a `overflow-x: auto` strip on its own. The "‹"/"›"
// arrows are ALWAYS rendered (not only past `visibleLimit`) and just disabled
// when there's nothing to slide to — every Variant's row then reserves the
// same width for label+arrows+strip regardless of how many options it has,
// so rows with few options line up with rows that need to scroll instead of
// the swatches starting at a different x position per row.
function VariantOptionRow({ variant, selected, onSelect, swatchSize, visibleLimit = DEFAULT_VISIBLE_LIMIT }: {
  variant: ProductVariantDTO;
  selected: string | undefined;
  onSelect: (optionId: string) => void;
  swatchSize: number;
  visibleLimit?: number;
}) {
  const rowRef = useRef<HTMLDivElement>(null);
  const drag = useRef({ isDown: false, moved: false, startX: 0, scrollLeft: 0 });

  const onMouseDown = (e: React.MouseEvent) => {
    const el = rowRef.current;
    if (!el) return;
    drag.current = { isDown: true, moved: false, startX: e.pageX, scrollLeft: el.scrollLeft };
  };
  const onMouseMove = (e: React.MouseEvent) => {
    const el = rowRef.current;
    if (!el || !drag.current.isDown) return;
    const dx = e.pageX - drag.current.startX;
    if (Math.abs(dx) > 3) drag.current.moved = true;
    el.scrollLeft = drag.current.scrollLeft - dx;
  };
  const endDrag = () => { drag.current.isDown = false; };
  // A drag that actually moved the strip shouldn't also fire the swatch's
  // click — capture-phase guard so the click never reaches `onSelect`.
  const onClickCapture = (e: React.MouseEvent) => {
    if (drag.current.moved) { e.stopPropagation(); drag.current.moved = false; }
  };
  const slide = (dir: 1 | -1) => {
    rowRef.current?.scrollBy({ left: dir * (swatchSize + ROW_GAP), behavior: 'smooth' });
  };

  const itemCount = variant.options.length + (variant.allowCustomColorPicker ? 1 : 0);
  const arrowsDisabled = itemCount <= visibleLimit;
  const visibleWidth = visibleLimit * swatchSize + (visibleLimit - 1) * ROW_GAP;
  const arrowBtnStyle: React.CSSProperties = {
    width: 22, height: 22, borderRadius: '50%', border: '1px solid #cbd5e1', background: '#ffffff',
    color: '#475569', fontSize: 13, fontWeight: 700, flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    cursor: arrowsDisabled ? 'not-allowed' : 'pointer', opacity: arrowsDisabled ? 0.35 : 1,
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4, minWidth: 0 }}>
      <button onClick={() => slide(-1)} disabled={arrowsDisabled} title="Previous options" style={arrowBtnStyle}>‹</button>
      <div
        ref={rowRef}
        className="no-scrollbar"
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={endDrag}
        onMouseLeave={endDrag}
        onClickCapture={onClickCapture}
        style={{
          display: 'flex', gap: ROW_GAP, overflowX: 'auto', cursor: 'grab',
          WebkitOverflowScrolling: 'touch', paddingBottom: 2, userSelect: 'none',
          maxWidth: visibleWidth,
        }}
      >
        {variant.options.map(o => {
          const isSelected = selected === o.id;
          const isMetallic = o.type === 'metallic';
          const isTexture = o.type === 'texture' && !!o.textureUrl;
          const background = isTexture
            ? `url(${o.textureUrl}) center/cover`
            : isMetallic && o.colorHex
              ? metallicBackground(o.colorHex)
              : (o.colorHex ?? '#d4d4d8');
          const shineShadow = isMetallic ? 'inset 0 2px 3px rgba(255,255,255,0.85), inset 0 -3px 5px rgba(0,0,0,0.35)' : null;
          const selectionShadow = isSelected ? '0 0 0 2px var(--selection-soft)' : null;
          return (
            <button
              key={o.id}
              onClick={() => onSelect(o.id)}
              title={isMetallic ? `${o.name} (metallic)` : o.name}
              style={{
                width: swatchSize, height: swatchSize, borderRadius: '50%', cursor: 'pointer', flexShrink: 0,
                background,
                border: isSelected ? '2px solid var(--selection)' : isMetallic ? '1px solid #94a3b8' : '1px solid #cbd5e1',
                boxShadow: [shineShadow, selectionShadow].filter(Boolean).join(', ') || 'none',
              }}
            />
          );
        })}
        {variant.allowCustomColorPicker && (
          <ColorPickerSwatch
            size={swatchSize}
            title={variant.label}
            value={selected?.startsWith('custom:') ? selected.slice(7) : undefined}
            onChange={hex => onSelect(`custom:${hex}`)}
          />
        )}
      </div>
      <button onClick={() => slide(1)} disabled={arrowsDisabled} title="More options" style={arrowBtnStyle}>›</button>
    </div>
  );
}
